import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllConfigs } from './redux/actions/configActions';
import ConfigEditorWithJsonEditor from './ConfigAdmin3';
import { 
  Container, Stack, Group, Title, Card, Text, Button, 
  Loader, Alert, Badge, ActionIcon, Divider 
} from '@mantine/core';
import { 
  IconClipboard, IconEdit, IconRobot, IconEye, IconBrain, 
  IconCurrencyDollar, IconShield, IconArrowLeft, IconSettings
} from '@tabler/icons-react';

const AdminConfigHome = () => {
  const dispatch = useDispatch();
  const configNames = useSelector((state) => state.config.configNames);
  const loading = useSelector((state) => state.config.loading);
  const error = useSelector((state) => state.config.error);

  const [selectedConfig, setSelectedConfig] = useState(null);
  const [editorType, setEditorType] = useState('jsoneditor'); // Default editor type

  useEffect(() => {
    dispatch(fetchAllConfigs());
  }, [dispatch]);

  const handleConfigSelect = (configName) => {
    setSelectedConfig(configName);
  };

  const handleBack = () => {
    setSelectedConfig(null);
  };

  // Helper function to convert config name to title case and add description
  const getConfigDisplayInfo = (configName) => {
    const configs = {
      'nlp_model_evaluation_config': {
        title: 'NLP Model Evaluation Config',
        description: 'Complete assessment template for natural language processing models with bias detection and fairness evaluation.',
        icon: IconBrain,
        color: 'blue'
      },
      'computer_vision_model_assessment_config': {
        title: 'Computer Vision Model Assessment Config', 
        description: 'Comprehensive evaluation framework for computer vision models including adversarial robustness testing.',
        icon: IconEye,
        color: 'green'
      },
      'generative_ai_risk_assessment_template': {
        title: 'Generative AI Risk Assessment Template',
        description: 'Advanced template for generative models with safety and alignment considerations.',
        icon: IconRobot,
        color: 'purple'
      },
      'financial_risk_model_template': {
        title: 'Financial Risk Model Template',
        description: 'Specialized framework for financial modeling with regulatory compliance checks.',
        icon: IconCurrencyDollar,
        color: 'yellow'
      }
    };

    return configs[configName] || {
      title: configName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      description: 'Risk assessment template for model evaluation and safety analysis.',
      icon: IconShield,
      color: 'gray'
    };
  };

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center" py={60}>
          <Loader size="lg" />
          <Text size="lg">Loading configurations...</Text>
        </Group>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Error" variant="filled">
          Error loading configurations: {error}
        </Alert>
      </Container>
    );
  }

  if (selectedConfig) {
    return (
      <Container size="lg" py="xl">
        <Stack gap="lg">
          <Group gap="md">
            <ActionIcon 
              variant="subtle" 
              size="lg"
              onClick={handleBack}
            >
              <IconArrowLeft size={18} />
            </ActionIcon>
            <Title order={2}>Edit Configuration</Title>
          </Group>

          <Group gap="md">
            <Button
              variant={editorType === 'jsoneditor' ? 'filled' : 'light'}
              leftSection={<IconSettings size={16} />}
              onClick={() => setEditorType('jsoneditor')}
            >
              JSON Editor
            </Button>
          </Group>

          {editorType === 'jsoneditor' && <ConfigEditorWithJsonEditor configName={selectedConfig} />}
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Group gap="md" align="center">
          <IconClipboard size={32} color="#1976d2" />
          <Title order={1}>Assessment Templates</Title>
        </Group>
        
        <Divider />

        <Stack gap="md">
          {configNames.map((configName) => {
            const info = getConfigDisplayInfo(configName);
            const IconComponent = info.icon;
            
            return (
              <Card
                key={configName}
                withBorder
                shadow="sm"
                padding="lg"
                radius="md"
                style={{ cursor: 'pointer' }}
                onClick={() => handleConfigSelect(configName)}
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Group gap="md" align="flex-start">
                    <IconComponent size={32} color={`var(--mantine-color-${info.color}-6)`} />
                    <Stack gap="xs" style={{ flex: 1 }}>
                      <Group gap="xs" align="center">
                        <Text fw={600} size="lg">{info.title}</Text>
                        <Badge color={info.color} variant="light" size="sm">
                          Template
                        </Badge>
                      </Group>
                      <Text size="sm" c="dimmed" lineClamp={2}>
                        {info.description}
                      </Text>
                    </Stack>
                  </Group>
                  
                  <Button
                    variant="light"
                    size="sm"
                    leftSection={<IconEdit size={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConfigSelect(configName);
                    }}
                  >
                    Edit
                  </Button>
                </Group>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    </Container>
  );
};

export default AdminConfigHome;
